import type { CalendarView } from "@ronin/contracts";
import {
  addDays,
  addMonths,
  dateParts,
  eachDay,
  isoDate,
  monthGrid,
  startOfWeek,
  type WeekStart,
} from "@ronin/core";

export interface VisibleRange {
  from: string;
  to: string;
  days: string[];
}

/** The days a view shows around `date`. */
export function visibleRange(view: CalendarView, date: string, weekStart: WeekStart): VisibleRange {
  let days: string[];
  if (view === "month") days = monthGrid(date, weekStart);
  else if (view === "day") days = [date];
  else days = eachDay(startOfWeek(date, weekStart), addDays(startOfWeek(date, weekStart), 6));
  return { from: days[0] ?? date, to: days[days.length - 1] ?? date, days };
}

/** The date one period before (-1) or after (+1). */
export function stepDate(view: CalendarView, date: string, direction: 1 | -1) {
  if (view === "month") return addMonths(date, direction);
  if (view === "day") return addDays(date, direction);
  return addDays(date, direction * 7);
}

const utc = (date: string) => {
  const { year, month, day } = dateParts(date);
  return new Date(Date.UTC(year, month - 1, day));
};
const format = (date: string, options: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat("en-GB", { ...options, timeZone: "UTC" }).format(utc(date));

/** "October 2026", "19 – 25 Oct 2026", "28 Sep – 4 Oct 2026", "Sat 24 Oct 2026". */
export function periodTitle(view: CalendarView, date: string, range: VisibleRange): string {
  if (view === "month") return format(date, { month: "long", year: "numeric" });
  if (view === "day") {
    const day = format(date, { day: "numeric", month: "short", year: "numeric" });
    return `${format(date, { weekday: "short" })} ${day}`;
  }
  const from = dateParts(range.from);
  const to = dateParts(range.to);
  const end = format(range.to, { day: "numeric", month: "short", year: "numeric" });
  if (from.year !== to.year) {
    return `${format(range.from, { day: "numeric", month: "short", year: "numeric" })} – ${end}`;
  }
  if (from.month !== to.month)
    return `${format(range.from, { day: "numeric", month: "short" })} – ${end}`;
  return `${from.day} – ${end}`;
}

/** Weekday names in display order, e.g. ["Mon", …, "Sun"]. */
export function weekdayLabels(weekStart: WeekStart): string[] {
  // 2026-10-19 is a Monday, 2026-10-18 a Sunday.
  const first = weekStart === "monday" ? isoDate(2026, 10, 19) : isoDate(2026, 10, 18);
  return eachDay(first, addDays(first, 6)).map((d) => format(d, { weekday: "short" }));
}

/** "Mon", for compact column headers. */
export const shortWeekday = (date: string) => format(date, { weekday: "short" });

/** "Mon 19", for column headers. */
export const dayLabel = (date: string) =>
  `${format(date, { weekday: "short" })} ${dateParts(date).day}`;

/** "24 October 2026", for screen readers. */
export const longDate = (date: string) =>
  format(date, { weekday: "long", day: "numeric", month: "long", year: "numeric" });

/** "30th" */
export function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  return `${n}${({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[n % 10] ?? "th"}`;
}
